// Supervisor override transient — cuando una request rebota con 403
// PERMISSION_DENIED, abrimos el PinLoginDialog para que un supervisor
// introduzca su PIN y AUTORIZAMOS la acción con sus credenciales.
// Tras ejecutarse, cerramos la sesión del supervisor en backend y
// restauramos la sesión del empleado original.
//
// Sin este helper: cada botón sensible tendría que envolver su
// dispatch con `useRequirePin({ action, run })`. Con este helper la
// autorización es AUTOMÁTICA en cualquier endpoint marcado con
// [RequireEmployeeAction], sin tocar los callers.

import { store } from "src/redux/store";
import {
  openPinDialog,
  setSession,
  clearSession,
} from "src/redux/slices/employeeSession";
import { showSnackbar } from "./globalSnackbar";

// Dedup: si dos requests idénticas rebotan (doble click) evitamos
// abrir el diálogo dos veces. Clave = método + url + hash del body.
const pendingOverrides = new Map();
const keyOf = (config) => `${config.method}|${config.url}|${
  typeof config.data === "string" ? config.data : JSON.stringify(config.data ?? "")
}`;

// Mutex — durante la ventana en la que el token del supervisor está
// activo en localStorage (reejecución + logout backend + restore),
// las demás requests deben ESPERAR o saldrían con el token del
// supervisor en vez del token del empleado original. La reejecución
// del override lleva la bandera `__isOverrideRetry` para saltarse
// esta espera (si no, se autobloquearía).
let overrideExecutionInFlight = null;

export function getOverrideMutex() {
  return overrideExecutionInFlight;
}

// Snapshot ligero de la sesión de empleado que hay AHORA. Se restaura
// tras la ejecución supervisorada.
export function snapshotEmployeeSession() {
  try {
    return {
      token: window.localStorage.getItem("EmployeeSessionToken"),
      info: window.localStorage.getItem("EmployeeSessionInfo"),
      expiresAt: window.localStorage.getItem("EmployeeSessionExpiresAt"),
    };
  } catch { return { token: null, info: null, expiresAt: null }; }
}

export function restoreEmployeeSession(snapshot) {
  try {
    if (snapshot?.token) {
      window.localStorage.setItem("EmployeeSessionToken", snapshot.token);
      window.localStorage.setItem("EmployeeSessionInfo", snapshot.info || "null");
      window.localStorage.setItem("EmployeeSessionExpiresAt", snapshot.expiresAt || "");
      let info = null;
      try { info = snapshot.info ? JSON.parse(snapshot.info) : null; } catch { /* ignore */ }
      store.dispatch(setSession({
        token: snapshot.token,
        expiresAt: snapshot.expiresAt || null,
        session: info,
      }));
    } else {
      store.dispatch(clearSession());
    }
  } catch { /* localStorage no disponible */ }
}

// Cierra la sesión del supervisor en backend. Se hace ANTES de restaurar
// el token del empleado original — así el POST viaja con el jti del
// supervisor y el registry lo marca revocado. Si no se hace, la
// sesión del supervisor queda "viva" 12h y aparece en "Sesiones
// activas" del admin como una identidad colgada.
export async function revokeSupervisorSession(axiosInstance) {
  try {
    await axiosInstance.post("employeeActiveSession/logout", null, {
      skipDedupe: true,
      skipIdempotency: true,
      __isOverrideRetry: true, // salta el mutex — parte de la ventana
    });
  } catch { /* si backend rechaza, la sesión caducará por TTL 12h */ }
}

/**
 * Llamado desde el interceptor axios cuando un 403 PERMISSION_DENIED
 * llega. Retorna una Promise que:
 *  - se RESUELVE con la respuesta de la re-ejecución si el supervisor
 *    autoriza correctamente Y tiene el permiso.
 *  - se RECHAZA con el error original si el supervisor cancela.
 *  - se RECHAZA con el error de la nueva request si el supervisor
 *    autoriza pero él tampoco tiene el permiso.
 */
export function requestSupervisorOverride(error, axiosInstance) {
  const config = error?.config;
  const body = error?.response?.data;
  if (!config || !body) return Promise.reject(body);

  const key = keyOf(config);
  if (pendingOverrides.has(key)) return pendingOverrides.get(key);

  const promise = new Promise((resolve, reject) => {
    const prev = snapshotEmployeeSession();
    const reason = body.message
      || `Se requiere autorización para: ${body.action || "acción restringida"}`;

    store.dispatch(openPinDialog({
      reason,
      action: body.action,
      // El override es una autorización EXPLÍCITA de un encargado
      // distinto. Forzamos el selector de empleado para que el
      // supervisor se identifique y el backend valide el PIN contra
      // SU perfil — no contra cualquier otro que casualmente lo
      // comparta. Sin esto, dos encargados con el mismo PIN podían
      // autorizar acciones como si fueran el otro (auditoría a nombre
      // del empleado equivocado).
      showEmployeeSelector: true,
      onSuccess: async () => {
        // Aquí el token del supervisor YA está en localStorage
        // (loginByPin lo guardó). Abrimos la "ventana crítica" con
        // el mutex → otras requests esperan hasta que restauremos.
        overrideExecutionInFlight = (async () => {
          try {
            const res = await axiosInstance.request({
              ...config,
              skipDedupe: true,
              __isOverrideRetry: true,
            });
            // Logout supervisor con SU token aún activo.
            await revokeSupervisorSession(axiosInstance);
            showSnackbar("Acción autorizada por supervisor", { variant: "success" });
            return res;
          } catch (retryErr) {
            // Igual cerramos la sesión del supervisor aunque falle
            // el retry — no queremos dejar el token colgado.
            await revokeSupervisorSession(axiosInstance);
            throw retryErr;
          } finally {
            restoreEmployeeSession(prev);
          }
        })();
        try {
          resolve(await overrideExecutionInFlight);
        } catch (err) {
          reject(err);
        } finally {
          overrideExecutionInFlight = null;
          pendingOverrides.delete(key);
        }
      },
      onCancel: () => {
        pendingOverrides.delete(key);
        reject(body);
      },
    }));
  });

  pendingOverrides.set(key, promise);
  return promise;
}
